// Supabase client — credentials must come from environment variables.
// Copy .env.example → .env.local and fill in VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const isProd = import.meta.env.PROD;

function deriveProjectUrlFromAnonKey(anonKey: string): string {
  try {
    const payload = anonKey.split('.')[1];
    if (!payload) return '';

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const normalized = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(normalized);
    const claims = JSON.parse(decoded) as { ref?: string; iss?: string };

    if (claims?.iss === 'supabase' && claims?.ref) {
      return `https://${claims.ref}.supabase.co`;
    }

    return '';
  } catch {
    return '';
  }
}

// The direct Supabase origin — used for Realtime (WebSocket) in all environments.
// Realtime cannot be proxied through a Vercel serverless function (no WS upgrade support).
const SUPABASE_ANON_KEY =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  '';

const SUPABASE_DIRECT_URL =
  import.meta.env.VITE_SUPABASE_URL ||
  deriveProjectUrlFromAnonKey(SUPABASE_ANON_KEY);

// Keep the client base URL pointed at the direct Supabase project origin.
// This SDK version derives Realtime from the base URL and does not support
// overriding websocket host through typed options.
const SUPABASE_URL = SUPABASE_DIRECT_URL;

if (!SUPABASE_DIRECT_URL || !SUPABASE_ANON_KEY) {
  console.warn(
    '[Supabase] Missing credentials. Copy .env.example → .env.local and set ' +
    'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY).'
  );
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'X-Client-Info': '@supabase/supabase-js',
    },
  },
  realtime: {
    timeout: 10000,
    params: {
      eventsPerSecond: 10,
    },
  },
});